'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { Building2, Users, Settings, Calendar, Copy, Check, Edit, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

function OrganizationOverviewSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function OrganizationOverview() {
  const { t } = useTranslation('settings');
  const { organization, membership, isLoaded } = useOrganization();
  const { userMemberships } = useOrganizationList({
    userMemberships: { pageSize: 100 },
  });
  const { toast } = useToast();

  if (!isLoaded) {
    return <OrganizationOverviewSkeleton />;
  }

  if (!organization) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Building2 className="text-muted-foreground mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-semibold">{t('organization.overview.noOrgSelected')}</h3>
          <p className="text-muted-foreground mt-2 text-sm">
            {t('organization.overview.pleaseSelectOrg')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (date: Date | number | string | null | undefined): string => {
    if (!date) return t('organization.overview.na');
    try {
      return format(new Date(date), 'MMMM dd, yyyy HH:mm');
    } catch (e) {
      return t('organization.overview.invalidDate');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'org:admin':
        return 'destructive';
      case 'org:member':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* Organization Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('organization.overview.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.overview.organizationName')}</label>
            <p className="text-lg font-semibold">{organization.name}</p>
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.overview.organizationId')}</label>
            <CopyableResourceBadge
              name={t('organization.overview.organizationId')}
              value={organization.id}
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.overview.slug')}</label>
            <CopyableResourceBadge
              name={t('organization.overview.slug')}
              value={organization.slug || t('organization.overview.na')}
              className="font-mono text-xs"
            />
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.overview.created')}</label>
            <p className="text-sm">{formatDate(organization.createdAt)}</p>
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.overview.lastUpdated')}</label>
            <p className="text-sm">{formatDate(organization.updatedAt)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Membership & Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('organization.membership.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.membership.yourRole')}</label>
            <Badge variant={getRoleColor(membership?.role || '')}>
              {membership?.role === 'org:admin' ? t('organization.membership.admin') : membership?.role === 'org:member' ? t('organization.membership.member') : membership?.role?.replace('org:', '') || t('organization.membership.member')}
            </Badge>
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.membership.memberSince')}</label>
            <p className="text-sm">{formatDate(membership?.createdAt)}</p>
          </div>

          <div className="space-y-2">
            <label className="text-muted-foreground text-sm font-medium">{t('organization.membership.totalMembers')}</label>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{organization.membersCount || 0}</span>
              {organization.maxAllowedMemberships && (
                <span className="text-muted-foreground text-sm">
                  / {organization.maxAllowedMemberships} {t('organization.membership.max')}
                </span>
              )}
            </div>
          </div>

          {membership?.permissions && membership.permissions.length > 0 && (
            <div className="space-y-2">
              <label className="text-muted-foreground text-sm font-medium">{t('organization.membership.permissions')}</label>
              <div className="flex flex-wrap gap-2">
                {membership.permissions.map((permission, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {permission}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {organization.publicMetadata && Object.keys(organization.publicMetadata).length > 0 && (
            <div className="space-y-2">
              <label className="text-muted-foreground text-sm font-medium">{t('organization.membership.publicMetadata')}</label>
              <div className="space-y-2">
                {Object.entries(organization.publicMetadata).map(([key, value]) => {
                  const displayValue =
                    typeof value === 'object' && value !== null
                      ? JSON.stringify(value, null, 2)
                      : String(value);

                  return (
                    <div key={key} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t(`organization.membership.metadataKeys.${key}`, { defaultValue: key })}:
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(displayValue);
                              toast({
                                title: t('organization.members.toast.copiedTitle'),
                                description: t('organization.members.toast.copiedDescription', { key }),
                              });
                            } catch (error) {
                              toast({
                                title: t('organization.members.toast.copyFailedTitle'),
                                description: t('organization.members.toast.copyFailedDescription'),
                                variant: 'destructive',
                              });
                            }
                          }}
                          className="h-6 w-6 p-0"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="bg-muted rounded-md p-3">
                        <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs">
                          {displayValue}
                        </pre>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
